/**
 * useStudentActivityCounters — Feb 2026.
 *
 * Small, self-contained hook that powers the Dynamic Activity Counter
 * system in the topbar hamburger + sidebar. Only fires for role=Student;
 * for every other role it returns null so the caller can no-op.
 *
 * Buckets:
 *   assignments  → GET /api/assignments-v2/my
 *                  count where my_state ∈ {required, drafting, required_resubmission}
 *                  AND assignment status !== 'Closed'
 *   exams        → GET /api/assessments-v2/my-active
 *                  count where !my_attempt OR my_attempt.status ∈ {Not_Started, In_Progress}
 *   announcements→ GET /api/admin-notifications/my/highlight (total_unread field)
 *
 * Refresh:
 *   • Poll every 45s while the tab is visible.
 *   • Any code path may dispatch `window.dispatchEvent(new CustomEvent('mesp:activity-refresh'))`
 *     to force an immediate re-fetch (e.g. right after assignment submit,
 *     exam submit, announcement open). Zero-config for callers.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const OPTS = { withCredentials: true };
const POLL_MS = 45000;

const PENDING_ASSIGNMENT_STATES = new Set(['required', 'drafting', 'required_resubmission']);
const OPEN_EXAM_ATTEMPT_STATES = new Set(['Not_Started', 'In_Progress']);

const isPendingAssignment = (a) => (
  PENDING_ASSIGNMENT_STATES.has(a?.my_state) && a?.status !== 'Closed'
);

const isPendingExam = (row) => {
  const att = row?.my_attempt;
  if (!att) return true;
  return OPEN_EXAM_ATTEMPT_STATES.has(att.status);
};

export const ACTIVITY_REFRESH_EVENT = 'mesp:activity-refresh';

export const triggerActivityRefresh = () => {
  try { window.dispatchEvent(new CustomEvent(ACTIVITY_REFRESH_EVENT)); } catch { /* SSR / older browsers */ }
};

export function useStudentActivityCounters(role) {
  const enabled = role === 'Student';
  const [counts, setCounts] = useState({ assignments: 0, exams: 0, announcements: 0, total: 0 });
  const cancelRef = useRef(false);

  const fetchCounts = useCallback(async () => {
    if (!enabled) return;
    try {
      const [aRes, eRes, nRes] = await Promise.allSettled([
        axios.get(`${API}/api/assignments-v2/my`, OPTS),
        axios.get(`${API}/api/assessments-v2/my-active`, OPTS),
        axios.get(`${API}/api/admin-notifications/my/highlight`, OPTS),
      ]);
      if (cancelRef.current) return;

      const assignments = aRes.status === 'fulfilled'
        ? (aRes.value.data?.assignments || []).filter(isPendingAssignment).length
        : 0;
      const exams = eRes.status === 'fulfilled'
        ? (eRes.value.data?.items || []).filter(isPendingExam).length
        : 0;
      const announcements = nRes.status === 'fulfilled'
        ? Number(nRes.value.data?.total_unread || 0)
        : 0;

      setCounts({ assignments, exams, announcements, total: assignments + exams + announcements });
    } catch {
      /* fail-quiet — badges simply won't update */
    }
  }, [enabled]);

  useEffect(() => {
    cancelRef.current = false;
    if (!enabled) return () => { cancelRef.current = true; };

    fetchCounts();
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') fetchCounts();
    }, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') fetchCounts(); };
    const onRefresh = () => fetchCounts();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener(ACTIVITY_REFRESH_EVENT, onRefresh);

    return () => {
      cancelRef.current = true;
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener(ACTIVITY_REFRESH_EVENT, onRefresh);
    };
  }, [enabled, fetchCounts]);

  return enabled ? counts : null;
}
